import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './NewsDetail.module.css';
import API_BASE_URL from '../../config/api';

const NewsDetail = () => {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const language = i18n.language;
  const [newsItem, setNewsItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  useEffect(() => {
    const controller = new AbortController();
    const lang = language === 'ar' ? 'ar' : 'en';

    const fetchNews = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/news/${id}?lang=${lang}`, {
          signal: controller.signal,
        });

        if (!res.ok) {
          setNewsItem(null);
          return;
        }

        const data = await res.json();
        setNewsItem(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load news article', error);
          setNewsItem(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNews();

    return () => controller.abort();
  }, [id, language]);

  if (loading) {
    return (
      <div className={styles.newsDetailPage}>
        <div className={styles.container}>
          <div className={styles.notFound}>
            <h1>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!newsItem) {
    return (
      <div className={styles.newsDetailPage}>
        <div className={styles.container}>
          <div className={styles.notFound}>
            <h1>{language === 'ar' ? 'الخبر غير موجود' : 'News Not Found'}</h1>
            <Link to="/media-center" className={styles.backBtn}>
              {language === 'ar' ? 'العودة إلى المركز الإعلامي' : 'Back to Media Center'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const fullContent = newsItem.content || newsItem.summary || '';

  return (
    <div className={styles.newsDetailPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <Link to="/media-center" className={styles.backLink}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <span>{language === 'ar' ? 'العودة' : 'Back'}</span>
          </Link>
          <h1 className={styles.heroTitle}>{newsItem.title}</h1>
          <div className={styles.heroMeta}>
            <span className={styles.date}>{formatDate(newsItem.date)}</span>
          </div>
        </div>
      </section>

      {/* News Content */}
      <section className={styles.contentSection}>
        <div className={styles.container}>
          <div className={styles.contentLayout}>
            <div className={styles.imageSection}>
              <div className={styles.imageWrapper}>
                {newsItem.image_url ? (
                  <img src={newsItem.image_url} alt={newsItem.title} className={styles.newsImage} />
                ) : (
                  <div style={{ width: '100%', height: 300, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>{language === 'ar' ? 'لا توجد صورة' : 'No Image'}</span>
                  </div>
                )}
                <div className={styles.imageBadge}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <span>{language === 'ar' ? 'صورة الخبر' : 'News Image'}</span>
                </div>
              </div>
            </div>
            <div className={styles.textSection}>
              <div className={styles.contentCard}>
                <div className={styles.contentHeader}>
                  <div className={styles.categoryBadge}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                    </svg>
                    <span>{language === 'ar' ? 'بيان صحفي' : 'Press Release'}</span>
                  </div>
                </div>
                <div className={styles.content}>
                  {fullContent.includes('<') ? (
                    <div dangerouslySetInnerHTML={{ __html: fullContent }} />
                  ) : (
                    fullContent.split('\n\n').map((paragraph, index) => {
                      const isList = /^[•\-\d]+/.test(paragraph.trim());
                      
                      if (isList) {
                        return (
                          <ul key={index} className={styles.contentList}>
                            {paragraph.split('\n').filter(line => line.trim()).map((item, itemIndex) => (
                              <li key={itemIndex}>{item.replace(/^[•\-\d]+\.?\s*/, '')}</li>
                            ))}
                          </ul>
                        );
                      }
                      return (
                        <p key={index} className={styles.paragraph}>
                          {paragraph}
                        </p>
                      );
                    })
                  )}
                </div>
                <div className={styles.contentFooter}>
                  <div className={styles.shareSection}>
                    <span className={styles.shareLabel}>{language === 'ar' ? 'شارك الخبر:' : 'Share News:'}</span>
                    <div className={styles.shareButtons}>
                      <button 
                        className={styles.shareBtn}
                        onClick={() => {
                          if (navigator.share) {
                            navigator.share({
                              title: newsItem.title,
                              text: newsItem.summary || '',
                              url: window.location.href
                            });
                          }
                        }}
                        aria-label="Share"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="18" cy="5" r="3"></circle>
                          <circle cx="6" cy="12" r="3"></circle>
                          <circle cx="18" cy="19" r="3"></circle>
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetail;
